import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API_URL from '@/config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Upload, Trash2, Image as ImageIcon, Save, Building2, Palette, FileText, KeyRound, Eye, EyeOff, Clock, Globe, MapPin } from 'lucide-react';
import { LeadNotificationPrefsCard } from '@/components/settings/LeadNotificationPrefsCard';
import { PlanCard } from '@/components/settings/PlanCard';
import { toast } from 'sonner';
import { formatApiError } from '@/utils/apiError';

const DEFAULT_PRIMARY = '#2F5E96';
const DEFAULT_SECONDARY = '#300122';
const PASSWORD_RE = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
// Staff roles get a stricter policy that MUST mirror _staff_password_issue()
// in backend/routes/password_reset.py. Installers keep the 8+letter+digit baseline.
const STAFF_ROLES = new Set(['admin', 'staff', 'owner']);
const STAFF_PASSWORD_MIN_LEN = 12;

function staffPasswordIssue(pw) {
  if ((pw || '').length < STAFF_PASSWORD_MIN_LEN) return `Staff passwords must be at least ${STAFF_PASSWORD_MIN_LEN} characters.`;
  if (!/[a-z]/.test(pw)) return 'Staff passwords must include a lowercase letter.';
  if (!/[A-Z]/.test(pw)) return 'Staff passwords must include an uppercase letter.';
  if (!/\d/.test(pw)) return 'Staff passwords must include a digit.';
  if (!/[^\w\s]/.test(pw)) return 'Staff passwords must include a symbol (e.g. ! ? # @).';
  return null;
}

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? '' : 's'} ago`;
  const y = Math.floor(mo / 12);
  return `${y} year${y === 1 ? '' : 's'} ago`;
}

function LastLoginBanner({ user }) {
  if (!user?.previous_last_login_at) return null;
  const when = timeAgo(user.previous_last_login_at);
  const ip = user.previous_last_login_ip;
  return (
    <Card className="p-3 border border-blue-200 bg-blue-50/60" data-testid="last-login-banner">
      <div className="flex items-center gap-3 text-sm text-blue-900">
        <Clock className="w-4 h-4 text-blue-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <strong>Last logged in:</strong> {when}
          {ip ? <> · IP <code className="text-blue-700">{ip}</code></> : null}
        </div>
        <span className="text-[10px] uppercase tracking-wider text-blue-700/80 hidden sm:inline">security</span>
      </div>
    </Card>
  );
}

function ChangePasswordCard() {
  const { user } = useAuth();
  const isStaff = STAFF_ROLES.has((user?.role || '').toLowerCase());
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  // Live per-keystroke strength hint — only surfaces for staff so
  // installers keep the friendlier UX. Empty string = all good.
  const staffIssue = isStaff && next ? staffPasswordIssue(next) : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next || !confirm) return;
    // Role-aware strength gate — staff take the stricter path.
    if (isStaff) {
      const issue = staffPasswordIssue(next);
      if (issue) {
        toast.error(issue);
        return;
      }
    } else if (!PASSWORD_RE.test(next)) {
      toast.error('New password must be at least 8 characters and include a letter and a number.');
      return;
    }
    if (next !== confirm) {
      toast.error('New passwords do not match.');
      return;
    }
    if (current === next) {
      toast.error('New password must be different from your current one.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Could not change password.');
      toast.success('Password updated.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="w-5 h-5 text-[#7A0B7A]" />
        <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Rotate your password without going through the email reset flow.
        {isStaff && (
          <span className="block mt-1 text-xs text-[#7A0B7A] font-semibold" data-testid="staff-password-policy-note">
            Staff accounts require {STAFF_PASSWORD_MIN_LEN}+ chars with upper &amp; lowercase, a digit, and a symbol.
          </span>
        )}
      </p>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="cp-current">Current password</Label>
          <div className="relative">
            <Input
              id="cp-current"
              type={show ? 'text' : 'password'}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              data-testid="cp-current"
              className="pr-10"
            />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label htmlFor="cp-new">New password</Label>
          <Input
            id="cp-new"
            type={show ? 'text' : 'password'}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder={isStaff ? `${STAFF_PASSWORD_MIN_LEN}+ chars, A-z, 0-9, symbol` : 'At least 8 chars, letter + digit'}
            autoComplete="new-password"
            minLength={isStaff ? STAFF_PASSWORD_MIN_LEN : 8}
            data-testid="cp-new"
            aria-describedby={staffIssue ? 'cp-new-hint' : undefined}
          />
          {staffIssue && (
            <p id="cp-new-hint" className="mt-1 text-xs text-rose-600" data-testid="cp-new-hint">
              {staffIssue}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="cp-confirm">Confirm new</Label>
          <Input
            id="cp-confirm"
            type={show ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repeat new password"
            autoComplete="new-password"
            minLength={8}
            data-testid="cp-confirm"
          />
        </div>
        <div className="md:col-span-3">
          <Button
            type="submit"
            disabled={busy}
            data-testid="cp-submit"
            className="bg-[#7A0B7A] hover:bg-[#5e0860] text-white"
          >
            <Save className="w-4 h-4 mr-1" />
            {busy ? 'Updating…' : 'Update password'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    company_website: '',
    vat_number: '',
    brand_primary_color: DEFAULT_PRIMARY,
    brand_secondary_color: DEFAULT_SECONDARY,
    quote_terms: '',
    quote_validity_days: 30,
    // Public installer profile (Phase 2)
    postcode: '',
    bio: '',
    years_in_business: '',
    coverage_radius_miles: '',
    is_public: false,
  });
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoCacheBust, setLogoCacheBust] = useState(0);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [profilePhotoCacheBust, setProfilePhotoCacheBust] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/settings`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setForm({
          company_name: data.company_name || '',
          company_address: data.company_address || '',
          company_phone: data.company_phone || '',
          company_email: data.company_email || '',
          company_website: data.company_website || '',
          vat_number: data.vat_number || '',
          brand_primary_color: data.brand_primary_color || DEFAULT_PRIMARY,
          brand_secondary_color: data.brand_secondary_color || DEFAULT_SECONDARY,
          quote_terms: data.quote_terms || '',
          quote_validity_days: data.quote_validity_days || 30,
          postcode: data.postcode || '',
          bio: data.bio || '',
          years_in_business: data.years_in_business ?? '',
          coverage_radius_miles: data.coverage_radius_miles ?? '',
          is_public: !!data.is_public,
        });
        if (data.company_logo_path) {
          setLogoUrl(`${API_URL}/api/settings/logo`);
        } else {
          setLogoUrl(null);
        }
        if (data.profile_photo_path) {
          setProfilePhotoUrl(`${API_URL}/api/settings/profile-photo`);
        } else {
          setProfilePhotoUrl(null);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          quote_validity_days: parseInt(form.quote_validity_days, 10) || 30,
          coverage_radius_miles: form.coverage_radius_miles === '' ? null : parseInt(form.coverage_radius_miles, 10) || null,
          years_in_business: form.years_in_business === '' ? null : parseInt(form.years_in_business, 10),
        }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Save failed');
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo too large. Max 5MB.');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/settings/logo`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Upload failed');
      setLogoUrl(`${API_URL}/api/settings/logo`);
      setLogoCacheBust(Date.now());
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleLogoRemove = async () => {
    if (!window.confirm('Remove the company logo?')) return;
    try {
      const res = await fetch(`${API_URL}/api/settings/logo`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Delete failed');
      setLogoUrl(null);
      toast.success('Logo removed');
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#27292E] to-[#3a3d45] text-white">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <button
              data-testid="settings-back-btn"
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Business Profile</h1>
              <p className="text-white/70 text-sm">Logo, brand colours &amp; quote defaults — used automatically on every PDF quote you generate.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-20 text-gray-500">Loading settings…</div>
        ) : (
          <div className="space-y-6">
            {/* Account code */}
            {user?.account_code && (
              <Card className="p-5 border-2 border-[#006400]/30 bg-[#006400]/5">
                <div className="flex flex-wrap items-center gap-4 justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide font-bold text-[#006400]">Your Account Code</p>
                    <p className="text-xs text-gray-600 mt-1">Quote this code when phoning Ox Resin so staff can pull up your account instantly.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-2xl font-bold text-[#006400] tracking-wider" data-testid="settings-account-code">{user.account_code}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-[#006400] text-[#006400] hover:bg-[#006400]/10"
                      onClick={() => {
                        navigator.clipboard.writeText(user.account_code);
                        toast.success('Account code copied');
                      }}
                      data-testid="settings-copy-account-code"
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            <LastLoginBanner user={user} />

            <Card className="p-4 flex flex-wrap items-center justify-between gap-3 border-[#006400]/30 cursor-pointer hover:shadow-md transition" onClick={() => navigate('/toolbox/contact-form')} data-testid="settings-contact-form-link">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#006400]/10 text-[#006400] flex items-center justify-center shrink-0"><Globe className="w-5 h-5" /></div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Website Contact Form</p>
                  <p className="text-xs text-gray-500">Embed a contact form on your site — enquiries land straight in your Lead CRM.</p>
                </div>
              </div>
              <Button type="button" variant="outline" size="sm">Set up</Button>
            </Card>

            <PlanCard />

            <LeadNotificationPrefsCard />

            {/* Company info */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-[#2F5E96]" />
                <h2 className="text-lg font-semibold text-gray-900">Company Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    data-testid="settings-company-name"
                    value={form.company_name}
                    onChange={(e) => handleField('company_name', e.target.value)}
                    placeholder="e.g. Ox Resin Ltd"
                  />
                </div>
                <div>
                  <Label htmlFor="vat_number">VAT Number</Label>
                  <Input
                    id="vat_number"
                    data-testid="settings-vat"
                    value={form.vat_number}
                    onChange={(e) => handleField('vat_number', e.target.value)}
                    placeholder="GB123456789"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="company_address">Address</Label>
                  <Textarea
                    id="company_address"
                    data-testid="settings-address"
                    rows={2}
                    value={form.company_address}
                    onChange={(e) => handleField('company_address', e.target.value)}
                    placeholder="Full registered address"
                  />
                </div>
                <div>
                  <Label htmlFor="company_phone">Phone</Label>
                  <Input
                    id="company_phone"
                    data-testid="settings-phone"
                    value={form.company_phone}
                    onChange={(e) => handleField('company_phone', e.target.value)}
                    placeholder="01865 ..."
                  />
                </div>
                <div>
                  <Label htmlFor="company_email">Contact Email</Label>
                  <Input
                    id="company_email"
                    data-testid="settings-email"
                    type="email"
                    value={form.company_email}
                    onChange={(e) => handleField('company_email', e.target.value)}
                    placeholder="hello@yourcompany.co.uk"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="company_website">Website</Label>
                  <Input
                    id="company_website"
                    data-testid="settings-website"
                    value={form.company_website}
                    onChange={(e) => handleField('company_website', e.target.value)}
                    placeholder="https://"
                  />
                </div>
              </div>
            </Card>

            {/* Change Password */}
            <ChangePasswordCard />

            {/* Brand */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Palette className="w-5 h-5 text-[#7A0B7A]" />
                <h2 className="text-lg font-semibold text-gray-900">Brand</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Logo upload */}
                <div className="md:col-span-1">
                  <Label>Company Logo</Label>
                  <div
                    data-testid="settings-logo-preview"
                    className="mt-2 aspect-video rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 overflow-hidden"
                  >
                    {logoUrl ? (
                      <img
                        src={`${logoUrl}?t=${logoCacheBust}`}
                        alt="Company logo"
                        className="max-w-full max-h-full object-contain p-2"
                      />
                    ) : (
                      <div className="text-gray-400 text-center p-4">
                        <ImageIcon className="w-8 h-8 mx-auto mb-1" />
                        <div className="text-xs">No logo uploaded</div>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={handleLogoSelect}
                    data-testid="settings-logo-input"
                  />
                  <div className="flex gap-2 mt-2">
                    <Button
                      data-testid="settings-logo-upload-btn"
                      onClick={() => fileRef.current?.click()}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      {logoUrl ? 'Replace' : 'Upload'}
                    </Button>
                    {logoUrl && (
                      <Button
                        data-testid="settings-logo-remove-btn"
                        onClick={handleLogoRemove}
                        variant="outline"
                        size="sm"
                        className="text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">PNG, JPG, SVG or WebP. Max 5MB.</p>
                </div>

                {/* Colours */}
                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 content-start">
                  <div>
                    <Label htmlFor="brand_primary_color">Primary Colour</Label>
                    <div className="flex gap-2 mt-1">
                      <input
                        id="brand_primary_color"
                        data-testid="settings-primary-color"
                        type="color"
                        value={form.brand_primary_color}
                        onChange={(e) => handleField('brand_primary_color', e.target.value)}
                        className="h-10 w-14 rounded cursor-pointer border border-gray-200"
                      />
                      <Input
                        value={form.brand_primary_color}
                        onChange={(e) => handleField('brand_primary_color', e.target.value)}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Used for headers & section titles.</p>
                  </div>
                  <div>
                    <Label htmlFor="brand_secondary_color">Secondary Colour</Label>
                    <div className="flex gap-2 mt-1">
                      <input
                        id="brand_secondary_color"
                        data-testid="settings-secondary-color"
                        type="color"
                        value={form.brand_secondary_color}
                        onChange={(e) => handleField('brand_secondary_color', e.target.value)}
                        className="h-10 w-14 rounded cursor-pointer border border-gray-200"
                      />
                      <Input
                        value={form.brand_secondary_color}
                        onChange={(e) => handleField('brand_secondary_color', e.target.value)}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Used for totals & accents.</p>
                  </div>

                  {/* Live PDF preview swatch */}
                  <div className="sm:col-span-2">
                    <Label>Preview</Label>
                    <div className="mt-2 rounded-lg border border-gray-200 overflow-hidden bg-white">
                      <div
                        className="px-4 py-3 text-white font-semibold"
                        style={{ backgroundColor: form.brand_primary_color }}
                      >
                        {form.company_name || 'Your Company'} — Quote Header
                      </div>
                      <div className="px-4 py-6 text-sm text-gray-700">
                        Scope of works, line items, etc. will appear here.
                      </div>
                      <div
                        className="px-4 py-3 text-white text-center font-bold"
                        style={{ backgroundColor: form.brand_secondary_color }}
                      >
                        TOTAL £0.00
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Quote defaults */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-[#E0671F]" />
                <h2 className="text-lg font-semibold text-gray-900">Quote Defaults</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="quote_validity_days">Validity (days)</Label>
                  <Input
                    id="quote_validity_days"
                    data-testid="settings-validity"
                    type="number"
                    min="1"
                    max="365"
                    value={form.quote_validity_days}
                    onChange={(e) => handleField('quote_validity_days', e.target.value)}
                  />
                </div>
                <div className="md:col-span-3">
                  <Label htmlFor="quote_terms">Terms & Conditions (appears on PDF footer)</Label>
                  <Textarea
                    id="quote_terms"
                    data-testid="settings-terms"
                    rows={4}
                    value={form.quote_terms}
                    onChange={(e) => handleField('quote_terms', e.target.value)}
                    placeholder="e.g. Payment terms: 50% deposit upon acceptance, balance due on completion. Warranty: 10 years on installation."
                  />
                </div>
              </div>
            </Card>

            {/* Public Installer Profile (Approved Installer Programme — Phase 2) */}
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <ImageIcon className="w-5 h-5 text-emerald-700" />
                <h2 className="text-lg font-bold">Public Installer Profile</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Optional. Filled-in profiles appear on the public <code>/installers</code> directory and rank in postcode searches when you&apos;re Ox Approved.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Postcode (private — used for proximity)</Label>
                  <Input
                    data-testid="settings-postcode"
                    value={form.postcode}
                    onChange={(e) => handleField('postcode', e.target.value)}
                    placeholder="e.g. OX1 1AA"
                    className={!(form.postcode || '').trim() ? 'border-amber-400 bg-amber-50 focus-visible:ring-amber-400' : ''}
                  />
                  {!(form.postcode || '').trim() && (
                    <p className="text-xs text-amber-700 mt-1 flex items-center gap-1" data-testid="settings-postcode-hint">
                      <MapPin className="w-3.5 h-3.5 shrink-0" /> Add your postcode to start receiving nearby leads — we can&apos;t match you to enquiries without it.
                    </p>
                  )}
                </div>
                <div>
                  <Label>How far will you travel? (miles)</Label>
                  <Input
                    data-testid="settings-coverage-radius"
                    type="number"
                    min="1"
                    max="500"
                    value={form.coverage_radius_miles}
                    onChange={(e) => handleField('coverage_radius_miles', e.target.value)}
                    placeholder="e.g. 30"
                  />
                  <p className="text-xs text-gray-500 mt-1">Ox staff only match you with leads inside this distance from your postcode. Default 30 miles.</p>
                </div>
                <div>
                  <Label>Years in business</Label>
                  <Input
                    data-testid="settings-years"
                    type="number"
                    min="0"
                    value={form.years_in_business}
                    onChange={(e) => handleField('years_in_business', e.target.value)}
                    placeholder="e.g. 8"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Short bio (max ~280 characters)</Label>
                  <Textarea
                    data-testid="settings-bio"
                    rows={3}
                    maxLength={400}
                    value={form.bio}
                    onChange={(e) => handleField('bio', e.target.value)}
                    placeholder="One paragraph about your business — what you do, where you cover, why customers choose you."
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Profile photo (you / your team — not the company logo)</Label>
                  <div className="flex items-center gap-4 mt-2">
                    {profilePhotoUrl ? (
                      <img
                        src={`${profilePhotoUrl}?v=${profilePhotoCacheBust}`}
                        alt="Profile"
                        className="w-20 h-20 object-cover rounded-full border-2 border-gray-200"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                        No photo
                      </div>
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="settings-profile-photo-input"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          const fd = new FormData();
                          fd.append('file', f);
                          const res = await fetch(`${API_URL}/api/settings/profile-photo`, {
                            method: 'POST', credentials: 'include', body: fd,
                          });
                          if (res.ok) {
                            setProfilePhotoUrl(`${API_URL}/api/settings/profile-photo`);
                            setProfilePhotoCacheBust(Date.now());
                            toast.success('Profile photo uploaded');
                          } else {
                            toast.error('Upload failed');
                          }
                        }}
                      />
                      <Button as="span" variant="outline" size="sm" className="cursor-pointer">
                        <Upload className="w-4 h-4 mr-1" /> Upload Photo
                      </Button>
                    </label>
                  </div>
                </div>

                <div className="md:col-span-2 flex items-center justify-between p-4 rounded-lg border-2 border-emerald-200 bg-emerald-50">
                  <div>
                    <div className="font-semibold text-emerald-900">List me on the public directory</div>
                    <div className="text-xs text-emerald-700">
                      When enabled (and you&apos;re Ox Approved in at least one section) you&apos;ll appear at <code>/installers</code> and in postcode searches.
                    </div>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="settings-is-public"
                      checked={form.is_public}
                      onChange={(e) => handleField('is_public', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="relative w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                  </label>
                </div>
              </div>
            </Card>

            <div className="flex justify-end gap-3">
              <Button
                data-testid="settings-cancel-btn"
                variant="outline"
                onClick={() => fetchSettings()}
              >
                Reset
              </Button>
              <Button
                data-testid="settings-save-btn"
                onClick={handleSave}
                disabled={saving}
                className="bg-[#2F5E96] hover:bg-[#2F5E96]/90"
              >
                <Save className="w-4 h-4 mr-1" />
                {saving ? 'Saving…' : 'Save Settings'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
